import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { slugify } from "@/lib/utils";
import { Plus, Pencil, Search, Trash, Maximize2, Package, Users, Factory, Tag, Wallet, UserCircle, Receipt, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { MDXEditor, headingsPlugin, listsPlugin, markdownShortcutPlugin, toolbarPlugin, BoldItalicUnderlineToggles, ListsToggle, BlockTypeSelect, Button as MDXToolbarButton, Separator, quotePlugin, CodeToggle, InsertTable, tablePlugin, linkPlugin, linkDialogPlugin, CreateLink, InsertAdmonition, directivesPlugin, AdmonitionDirectiveDescriptor, codeBlockPlugin, InsertThematicBreak, thematicBreakPlugin } from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { TablePagination } from "@/components/ui/table-pagination";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { EntitiesIndexer } from "@/services/entities-indexer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type KB = { id: string; tenant_id: string; slug: string; nome: string; descricao: string | null; status: string };

export default function TrainingIA() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [items, setItems] = useState<KB[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<KB | null>(null);
  const [form, setForm] = useState<Partial<KB>>({ status: "ativo" });
  const [filterTerm, setFilterTerm] = useState("");
  const [activeKbId, setActiveKbId] = useState<string>("");
  const [restoreKbId, setRestoreKbId] = useState<string>("");
  const [texts, setTexts] = useState<Array<{ id: string; titulo: string | null; conteudo: string; status: string; last_indexed_at: string | null }>>([]);
  const [textForm, setTextForm] = useState<{ titulo?: string | null; conteudo?: string; status?: string }>({ status: "ativo" });
  const [docs, setDocs] = useState<Array<{ id: string; filename: string; mime_type: string; source_url: string; last_indexed_at?: string | null }>>([]);
  const [file, setFile] = useState<File | null>(null);
  const [driveFileId, setDriveFileId] = useState<string>("");
  const [driveFileUrl, setDriveFileUrl] = useState<string>("");
  const [driveIndexes, setDriveIndexes] = useState<Array<{ entity_id: string; title: string | null; source_updated_at: string | null; metadata: any }>>([]);
  const [indexingDrive, setIndexingDrive] = useState(false);
  const [driveModalOpen, setDriveModalOpen] = useState(false);
  const [googleLinked, setGoogleLinked] = useState(false);
  const [oauthConflict, setOauthConflict] = useState(false);
  const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const GOOGLE_API_KEY = (import.meta as any).env?.VITE_GOOGLE_API_KEY as string | undefined;
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [driveSearch, setDriveSearch] = useState<string>("");
  const [driveFiles, setDriveFiles] = useState<Array<{ id: string; name: string; modifiedTime: string }>>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const canUse = useMemo(() => !!tenantId, [tenantId]);
  const [fullscreenEditor, setFullscreenEditor] = useState<{ title: string; value: string; onChange: (v: string)=>void } | null>(null);
  const [lastEntityIndex, setLastEntityIndex] = useState<Record<string, { at: string; count: number }>>({});
  const [indexingType, setIndexingType] = useState<string | null>(null);
  const entityLabels: Record<string, string> = {
    products: "Produtos",
    clients: "Clientes",
    suppliers: "Fornecedores",
    expense_categories: "Categorias de Despesa",
    bank_accounts: "Contas Bancárias",
    profiles: "Usuários",
    transactions: "Transações",
  };
  const entityIcons: Record<string, JSX.Element> = {
    products: <Package className="h-4 w-4" />,
    clients: <Users className="h-4 w-4" />,
    suppliers: <Factory className="h-4 w-4" />,
    expense_categories: <Tag className="h-4 w-4" />,
    bank_accounts: <Wallet className="h-4 w-4" />,
    profiles: <UserCircle className="h-4 w-4" />,
    transactions: <Receipt className="h-4 w-4" />,
  };

  useEffect(() => {
    const load = async () => {
      if (!tenantId) return;
      const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId);
      setItems((data as any[]) || []);
    };
    load();
  }, [tenantId]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(async () => {
      await refreshGoogleLinked();
    });
    return () => {
      try { (data as any)?.subscription?.unsubscribe?.(); } catch {}
    };
  }, []);

  useEffect(() => {
    const loadDetails = async () => {
      if (!tenantId || !activeKbId || !open) return;
      const { data: t } = await supabase
        .from("kb_texts" as any)
        .select("id,titulo,conteudo,status,last_indexed_at")
        .eq("tenant_id", tenantId)
        .eq("kb_id", activeKbId)
        .order("created_at", { ascending: false });
      setTexts((t as any[]) || []);
      const { data: d } = await supabase
        .from("kb_documents" as any)
        .select("id,filename,mime_type,source_url,last_indexed_at")
        .eq("tenant_id", tenantId)
        .eq("kb_id", activeKbId)
        .eq("status", "ativo");
      setDocs((d as any[]) || []);
      const { data: di } = await supabase
        .from("ai_search_index" as any)
        .select("entity_id,title,source_updated_at,metadata")
        .eq("tenant_id", tenantId)
        .eq("entity_type", "kb_chunk")
        .contains("metadata", { kb_id: activeKbId })
        .not("metadata->>drive_file_id", "is", null)
        .order("source_updated_at", { ascending: false });
      setDriveIndexes((di as any[]) || []);
    };
    loadDetails();
  }, [tenantId, activeKbId, open]);

  const filtered = useMemo(() => {
    const t = filterTerm.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) => (it.nome?.toLowerCase().includes(t) || (it.slug || "").toLowerCase().includes(t) || (it.descricao || "").toLowerCase().includes(t)));
  }, [items, filterTerm]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pageItems = filtered.slice(startIndex, startIndex + itemsPerPage);

  const saveKb = async () => {
    if (!tenantId) return;
    const payload = { tenant_id: tenantId, slug: form.slug || (form.nome ? slugify(form.nome) : undefined), nome: form.nome, descricao: form.descricao, status: form.status || "ativo" } as any;
    let created: any = null;
    if (editing) {
      await supabase.from("knowledge_bases" as any).update(payload).eq("id", editing.id);
    } else {
      const { data: ins } = await supabase.from("knowledge_bases" as any).insert(payload).select("*").single();
      created = ins as any;
    }
    const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId);
    setItems((data as any[]) || []);
    if (created?.id) {
      setEditing(created as KB);
      setForm(created as Partial<KB>);
      setActiveKbId(created.id as string);
      setOpen(true);
    } else {
      setOpen(true);
    }
  };

  const addText = async () => {
    if (!tenantId || !activeKbId || !textForm.conteudo) return;
    const payload = { tenant_id: tenantId, kb_id: activeKbId, titulo: textForm.titulo, conteudo: textForm.conteudo, status: textForm.status || "ativo" } as any;
    await supabase.from("kb_texts" as any).insert(payload);
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
    setTextForm({ status: "ativo" });
  };

  const addTextAndIndex = async () => {
    if (!tenantId || !activeKbId || !textForm.conteudo) return;
    const payload = { tenant_id: tenantId, kb_id: activeKbId, titulo: textForm.titulo, conteudo: textForm.conteudo, status: textForm.status || "ativo" } as any;
    const { data: inserted } = await supabase
      .from("kb_texts" as any)
      .insert(payload)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .single();
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
    setTextForm({ status: "ativo" });
    if (inserted) {
      await indexText(inserted as any);
    }
  };

  const indexText = async (t: { id: string; titulo: string | null; conteudo: string }) => {
    const { error } = await supabase.functions.invoke("ai-index-text", { body: { tenant_id: tenantId, kb_id: activeKbId, text_id: t.id, title: t.titulo, content: t.conteudo } } as any);
    if (!error) {
      const { data } = await supabase
        .from("kb_texts" as any)
        .select("id,titulo,conteudo,status,last_indexed_at")
        .eq("tenant_id", tenantId)
        .eq("kb_id", activeKbId)
        .order("created_at", { ascending: false });
      setTexts((data as any[]) || []);
    }
  };

  const uploadDoc = async () => {
    if (!tenantId || !activeKbId || !file) return;
    const path = `${tenantId}/${activeKbId}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from("kb-documents" as any).upload(path, file);
    if (error) return;
    await supabase.from("kb_documents" as any).insert({ tenant_id: tenantId, kb_id: activeKbId, source_url: path, filename: file.name, mime_type: file.type, status: "ativo" });
    const { data } = await supabase
      .from("kb_documents" as any)
      .select("id,filename,mime_type,source_url,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .eq("status", "ativo");
    setDocs((data as any[]) || []);
    setFile(null);
  };

  const indexDoc = async (d: { id: string; source_url: string; filename: string; mime_type: string }) => {
    const signed = await supabase.storage.from("kb-documents" as any).createSignedUrl(d.source_url, 3600);
    if (!signed?.data?.signedUrl) return;
    await supabase.functions.invoke("ai-index-file", { body: { tenant_id: tenantId, kb_id: activeKbId, file_url: signed.data.signedUrl, filename: d.filename, mime_type: d.mime_type } } as any);
  };

  const deleteTraining = async (kb: KB) => {
    if (!tenantId) return;
    await supabase.from("ai_search_index" as any).delete().contains("metadata", { kb_id: kb.id }).eq("tenant_id", tenantId);
    await supabase.from("kb_texts" as any).delete().eq("tenant_id", tenantId).eq("kb_id", kb.id);
    await supabase.from("kb_documents" as any).delete().eq("tenant_id", tenantId).eq("kb_id", kb.id);
    await supabase.from("knowledge_bases" as any).delete().eq("tenant_id", tenantId).eq("id", kb.id);
    const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId);
    setItems((data as any[]) || []);
  };

  const deleteText = async (t: { id: string }) => {
    if (!tenantId || !activeKbId) return;
    await supabase.from("ai_search_index" as any).delete().contains("metadata", { kb_id: activeKbId, text_id: t.id }).eq("tenant_id", tenantId);
    await supabase.from("kb_texts" as any).delete().eq("tenant_id", tenantId).eq("id", t.id);
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
  };

  const deleteDoc = async (d: { id: string; filename: string }) => {
    if (!tenantId || !activeKbId) return;
    await supabase.from("ai_search_index" as any).delete().contains("metadata", { kb_id: activeKbId, filename: d.filename }).eq("tenant_id", tenantId);
    await supabase.from("kb_documents" as any).delete().eq("tenant_id", tenantId).eq("id", d.id);
    const { data } = await supabase
      .from("kb_documents" as any)
      .select("id,filename,mime_type,source_url,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .eq("status", "ativo");
    setDocs((data as any[]) || []);
  };

  const deleteDriveIndexes = async () => {
    if (!tenantId || !activeKbId) return;
    const meta: any = { kb_id: activeKbId };
    if (driveFileId) meta.drive_file_id = driveFileId;
    await supabase.from("ai_search_index" as any).delete().contains("metadata", meta).eq("tenant_id", tenantId);
    const { data: di } = await supabase
      .from("ai_search_index" as any)
      .select("entity_id,title,source_updated_at,metadata")
      .eq("tenant_id", tenantId)
      .eq("entity_type", "kb_chunk")
      .contains("metadata", { kb_id: activeKbId })
      .not("metadata->>drive_file_id", "is", null)
      .order("source_updated_at", { ascending: false });
    setDriveIndexes((di as any[]) || []);
  };

  const deleteDriveIndex = async (entityId: string) => {
    if (!tenantId) return;
    await supabase.from("ai_search_index" as any).delete().eq("tenant_id", tenantId).eq("entity_id", entityId);
    const { data: di } = await supabase
      .from("ai_search_index" as any)
      .select("entity_id,title,source_updated_at,metadata")
      .eq("tenant_id", tenantId)
      .eq("entity_type", "kb_chunk")
      .contains("metadata", { kb_id: activeKbId })
      .not("metadata->>drive_file_id", "is", null)
      .order("source_updated_at", { ascending: false });
    setDriveIndexes((di as any[]) || []);
  };

  

  const indexDrive = async () => {
    if (!driveFileId && !driveFileUrl) return;
    setIndexingDrive(true);
    const { error } = await (supabase.functions.invoke("ai-index-drive", { body: { tenant_id: tenantId, kb_id: activeKbId, file_id: driveFileId || undefined, file_url: driveFileUrl || undefined } } as any) as Promise<any>);
    setIndexingDrive(false);
    if (!error) {
      const { data: di } = await supabase
        .from("ai_search_index" as any)
        .select("entity_id,title,source_updated_at,metadata")
        .eq("tenant_id", tenantId)
        .eq("entity_type", "kb_chunk")
        .contains("metadata", { kb_id: activeKbId })
        .not("metadata->>drive_file_id", "is", null)
        .order("source_updated_at", { ascending: false });
      setDriveIndexes((di as any[]) || []);
    }
  };

  const refreshGoogleLinked = async () => {
    const { data } = await supabase.auth.getUser();
    const identities = (data?.user as any)?.identities || [];
    const linked = identities.some((i: any) => i.provider === "google");
    setGoogleLinked(linked);
  };

  const connectGoogleDrive = async () => {
    const linkRes = await supabase.auth.linkIdentity({ provider: "google" as any } as any);
    if (!linkRes.error) {
      await refreshGoogleLinked();
      return;
    }
    const code = (linkRes.error as any)?.status || (linkRes.error as any)?.code || "";
    const msg = (linkRes.error as any)?.message || "";
    if (String(code).includes("manual_linking_disabled") || String(msg).includes("manual_linking_disabled")) {
      toast("Ative 'Manual linking' no provedor Google em Supabase Auth");
      return;
    }
    if (String(code).includes("identity_already_exists") || String(msg).includes("identity_already_exists")) {
      setOauthConflict(true);
      return;
    }
    toast("Falha ao integrar Google (linkIdentity)");
  };

  const continueWithGoogleOAuth = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google" as any,
      options: { redirectTo: window.location.origin, queryParams: { access_type: "offline", prompt: "consent" }, skipBrowserRedirect: true },
    } as any);
    if (!error && data?.url) {
      window.open(data.url, "_blank", "noopener,noreferrer");
    }
  };

  const ensureGoogleToken = async () => {
    if (!GOOGLE_CLIENT_ID || !GOOGLE_API_KEY) {
      toast("Configurar VITE_GOOGLE_CLIENT_ID e VITE_GOOGLE_API_KEY");
      return null;
    }
    const loadScript = (src: string) => new Promise<void>((resolve) => {
      const s = document.createElement("script");
      s.src = src; s.async = true; s.onload = () => resolve(); document.head.appendChild(s);
    });
    if (!(window as any).google?.accounts?.oauth2) {
      await loadScript("https://accounts.google.com/gsi/client");
    }
    const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: "https://www.googleapis.com/auth/drive.readonly",
      callback: (resp: any) => { setGoogleToken(resp.access_token); },
    });
    tokenClient.requestAccessToken({ prompt: "consent" });
    return tokenClient;
  };

  const searchDriveDocs = async () => {
    setDriveLoading(true);
    if (!googleToken) await ensureGoogleToken();
    const token = googleToken;
    if (!token) { setDriveLoading(false); return; }
    const q = [`mimeType='application/vnd.google-apps.document'`, "trashed=false"];
    if (driveSearch.trim()) q.push(`name contains '${driveSearch.trim().replace(/'/g, "\'")}'`);
    const url = `https://www.googleapis.com/drive/v3/files?fields=files(id,name,modifiedTime)&orderBy=modifiedTime desc&pageSize=50&q=${encodeURIComponent(q.join(" and "))}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setDriveFiles((data.files || []).map((f: any) => ({ id: f.id, name: f.name, modifiedTime: f.modifiedTime })));
    } finally {
      setDriveLoading(false);
    }
  };

  useEffect(() => {
    try {
      const flag = localStorage.getItem("training_keep_open");
      const kb = localStorage.getItem("training_keep_open_kb") || "";
      if (flag === "1" && kb) {
        setRestoreKbId(kb);
      }
      localStorage.removeItem("training_keep_open");
      localStorage.removeItem("training_keep_open_kb");
    } catch {}
  }, []);

  useEffect(() => {
    if (restoreKbId && items.length > 0) {
      const it = items.find((x) => x.id === restoreKbId) || null;
      if (it) {
        setEditing(it);
        setForm(it as any);
        setActiveKbId(it.id);
        setOpen(true);
      }
      setRestoreKbId("");
    }
  }, [restoreKbId, items]);

  if (!canUse) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Nenhum tenant selecionado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Treinamento IA</h1>
          <p className="text-muted-foreground">Gerencie seus treinamentos de IA</p>
        </div>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">Treinar Cadastros {indexingType && (<Loader2 className="h-4 w-4 animate-spin" />)}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(["products","clients","suppliers","expense_categories","bank_accounts","profiles","transactions"] as const).map((t) => (
                <DropdownMenuItem key={t} onClick={async () => {
                  if (!tenantId || indexingType) return;
                  try {
                    setIndexingType(t);
                    const res = await EntitiesIndexer.batch({ tenant_id: tenantId, entity_type: t });
                    setLastEntityIndex((prev) => ({ ...prev, [t]: { at: new Date().toISOString(), count: (res as any)?.indexed || 0 } }));
                    toast.success(`${entityLabels[t]} indexado(s): ${(res as any)?.indexed || 0}`);
                  } catch (e: any) {
                    toast(String(e?.message || 'Falha ao indexar'));
                  } finally {
                    setIndexingType(null);
                  }
                }}>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      {entityIcons[t]}
                      <span>{entityLabels[t]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{lastEntityIndex[t]?.count ?? 0}</Badge>
                      {indexingType === t && (<Loader2 className="h-4 w-4 animate-spin" />)}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && editing?.id) setActiveKbId(editing.id); }}>
            <Button onClick={() => { navigate('/ia/treinamento-ia/novo'); }}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Treinamento
            </Button>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Editar Treinamento" : "Novo Treinamento"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3 grid-cols-8 items-end">
                <div className="space-y-1 col-span-2">
                  <Label htmlFor="kb-nome">Nome</Label>
                  <Input id="kb-nome" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value, slug: slugify(e.target.value) })} />
                </div>
                <div className="space-y-1 col-span-1">
                  <Label>Status</Label>
                  <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">ativo</SelectItem>
                      <SelectItem value="inativo">inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-4">
                  <Label htmlFor="kb-desc">Descrição</Label>
                  <Input id="kb-desc" value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button onClick={saveKb}>Salvar</Button>
                </div>
              </div>
              {!activeKbId ? (
                <div className="mt-4 text-sm text-muted-foreground">Salve o treinamento para habilitar Textos.</div>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-6">
                  <div className="col-span-1">
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label htmlFor="kb-text-title">Título</Label>
                          <Input id="kb-text-title" value={textForm.titulo || ""} onChange={(e) => setTextForm({ ...textForm, titulo: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="kb-text-content">Conteúdo</Label>
                          <div className="rounded border mdx-container">
                            <MDXEditor
                              markdown={textForm.conteudo || ""}
                              onChange={(v) => setTextForm({ ...textForm, conteudo: v })}
                              plugins={[
                                headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                                listsPlugin(),
                                markdownShortcutPlugin(),
                                quotePlugin(),
                                linkPlugin(),
                                linkDialogPlugin(),
                                tablePlugin(),
                                directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                                codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                                thematicBreakPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <div className="flex items-center gap-2">
                                      <BlockTypeSelect />
                                      <BoldItalicUnderlineToggles />
                                      <CodeToggle />
                                      <ListsToggle />
                                      <CreateLink />
                                      <InsertTable />
                                      <InsertAdmonition />
                                      <InsertThematicBreak />
                                      <Separator />
                                      <MDXToolbarButton onClick={() => setFullscreenEditor({ title: "Conteúdo", value: textForm.conteudo || "", onChange: (v) => setTextForm({ ...textForm, conteudo: v }) })}>
                                        <Maximize2 className="h-4 w-4" />
                                      </MDXToolbarButton>
                                    </div>
                                  ),
                                }),
                              ]}
                              className="min-h-[240px]"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <Button onClick={addTextAndIndex} disabled={!textForm.conteudo}>Adicionar Texto</Button>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                  <div className="col-span-2 overflow-y-auto max-h-[50vh] pr-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Título</TableHead>
                          <TableHead>Última Indexação</TableHead>
                          <TableHead>Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {texts.map((t) => (
                          <TableRow key={`text-${t.id}`}>
                            <TableCell>{t.titulo || "-"}</TableCell>
                            <TableCell>{t.last_indexed_at ? new Date(t.last_indexed_at).toLocaleString() : "-"}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button onClick={() => indexText(t)}>Indexar</Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                                      <Trash className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir</AlertDialogTitle>
                                      <AlertDialogDescription>Esta ação é irreversível. Confirmar?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteText(t)}>Excluir</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
          </DialogContent>
        </Dialog>
      </div>
    </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Lista de Treinamentos</CardTitle>
            <div className="relative w-full sm:w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar..." value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)} className="pl-8" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.nome}</TableCell>
                  <TableCell>{it.status}</TableCell>
                  <TableCell>{it.descricao || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => { navigate(`/ia/treinamento-ia/${it.id}`); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Treinamento</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação é irreversível. Confirmar?</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteTraining(it)}>Excluir</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">Nenhuma base cadastrada</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="mt-4">
            <TablePagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={filtered.length}
              totalPages={Math.ceil(filtered.length / itemsPerPage)}
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={driveModalOpen} onOpenChange={setDriveModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Adicionar do Google Drive</DialogTitle>
            <DialogDescription>Selecione um arquivo do Google Drive para indexar</DialogDescription>
          </DialogHeader>
          {!googleLinked ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Sua conta não está integrada ao Google Drive.</div>
              <Button onClick={connectGoogleDrive}>Integrar Google Drive</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="drive-file-id-modal">Drive File ID</Label>
                  <Input id="drive-file-id-modal" value={driveFileId} onChange={(e) => setDriveFileId(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="drive-file-url-modal">Drive File URL (pública/assinada)</Label>
                  <Input id="drive-file-url-modal" value={driveFileUrl} onChange={(e) => setDriveFileUrl(e.target.value)} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Input placeholder="Buscar Google Docs" value={driveSearch} onChange={(e) => setDriveSearch(e.target.value)} className="w-[260px]" />
                  <Button variant="outline" onClick={searchDriveDocs} disabled={driveLoading}>Buscar</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={deleteDriveIndexes} disabled={!driveFileId && !driveFileUrl}>Excluir índices</Button>
                  <Button onClick={indexDrive} disabled={indexingDrive || (!driveFileId && !driveFileUrl)} className="bg-blue-600 text-white hover:bg-blue-700">{indexingDrive ? "Indexando..." : "Indexar"}</Button>
                </div>
              </div>
              {driveFiles.length > 0 && (
                <div className="max-h-[40vh] overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Título</TableHead>
                        <TableHead>Última Modificação</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driveFiles.map((f) => (
                        <TableRow key={`gdoc-${f.id}`}>
                          <TableCell>{f.name}</TableCell>
                          <TableCell>{new Date(f.modifiedTime).toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => { setDriveFileId(f.id); setDriveFileUrl(`https://docs.google.com/document/d/${f.id}/export?format=html`); }}>Selecionar</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="max-h-[40vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Última Indexação</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {driveIndexes.map((di) => (
                      <TableRow key={`drive-modal-${di.entity_id}`}>
                        <TableCell>Drive</TableCell>
                        <TableCell>{di.title || "-"}</TableCell>
                        <TableCell>{di.source_updated_at ? new Date(di.source_updated_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                                <Trash className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir índice</AlertDialogTitle>
                                <AlertDialogDescription>Esta ação é irreversível. Confirmar?</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteDriveIndex(di.entity_id)}>Excluir</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={oauthConflict} onOpenChange={setOauthConflict}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conta Google já vinculada</AlertDialogTitle>
            <AlertDialogDescription>
              Esta conta Google já está vinculada a outro usuário. Você pode entrar com Google nesta sessão (a sessão atual será trocada), ou cancelar e manter a sessão atual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOauthConflict(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={continueWithGoogleOAuth}>Entrar com Google</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {open && (
        <div className="hidden" />
      )}
      {Object.keys(lastEntityIndex).length > 0 && (
        <div className="p-2 text-xs text-muted-foreground">Últimas indexações: {Object.entries(lastEntityIndex).map(([k,v]) => `${k}: ${v.count} em ${new Date(v.at).toLocaleString()}`).join(" · ")}</div>
      )}
      {fullscreenEditor && (
        <Dialog open onOpenChange={(open) => { if (!open) setFullscreenEditor(null); }}>
          <DialogContent className="sm:max-w-[95vw] w-[95vw] h-[90vh]">
            <DialogHeader>
              <DialogTitle>{fullscreenEditor.title}</DialogTitle>
            </DialogHeader>
            <div className="rounded border mdx-container h-full">
              <MDXEditor
                markdown={fullscreenEditor.value || ""}
                onChange={(v) => setFullscreenEditor({ ...fullscreenEditor, value: v })}
                plugins={[
                  headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                  listsPlugin(),
                  markdownShortcutPlugin(),
                  quotePlugin(),
                  linkPlugin(),
                  linkDialogPlugin(),
                  tablePlugin(),
                  directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                  codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                  thematicBreakPlugin(),
                ]}
                className="h-[70vh]"
              />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => { fullscreenEditor.onChange(fullscreenEditor.value); setFullscreenEditor(null); }}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
